import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import SEOHead from '../../components/SEOHead';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

const privacyJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/privacidade`,
  name: 'Política de Privacidade — EntreNós',
  description:
    'Saiba como o EntreNós coleta, usa e protege seus dados pessoais de acordo com a LGPD (Lei Geral de Proteção de Dados).',
  url: `${SITE_URL}/privacidade`,
  inLanguage: 'pt-BR',
  dateModified: '2026-04-09',
  isPartOf: {
    '@type': 'WebSite',
    name: 'EntreNós',
    url: SITE_URL,
  },
  about: {
    '@type': 'Organization',
    name: 'EntreNós',
    url: SITE_URL,
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Política de Privacidade', item: `${SITE_URL}/privacidade` },
    ],
  },
};

export default function PrivacyPage() {
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const sections = [
    { id: 'introducao', label: 'Introdução' },
    { id: 'dados-coletados', label: 'Dados Coletados' },
    { id: 'uso-dados', label: 'Uso dos Dados' },
    { id: 'compartilhamento', label: 'Compartilhamento' },
    { id: 'seguranca', label: 'Segurança' },
    { id: 'direitos', label: 'Seus Direitos' },
    { id: 'cookies', label: 'Cookies' },
    { id: 'menores', label: 'Menores de Idade' },
    { id: 'alteracoes', label: 'Alterações' },
    { id: 'contato', label: 'Contato' },
  ];

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
      <SEOHead
        title="Política de Privacidade — EntreNós"
        description="Saiba como o EntreNós coleta, usa e protege seus dados pessoais conforme a LGPD. Leia nossa política completa de privacidade e proteção de dados."
        keywords="política de privacidade EntreNós, LGPD, proteção de dados casais, privacidade plataforma"
        canonical="/privacidade"
        schemaJsonLd={privacyJsonLd}
        dateModified="2026-04-09"
      />
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/1b0e966215e432d351b2e9c11ca0b10e/154dcc8a65fed03ee4761cc080c3eddf.png"
                alt="EntreNós"
                fetchPriority="high"
                decoding="async"
                width="120"
                height="40"
                className="h-10 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-gray-100 hover:shadow-md text-sm font-medium"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="whitespace-nowrap">Voltar ao início</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-rose-200 mb-6">
            <i className="ri-shield-check-fill text-rose-500"></i>
            <span className="text-sm font-medium text-gray-700">Sua privacidade é nossa prioridade</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Política de{' '}
            <span className="bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent">
              Privacidade
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Entenda como coletamos, usamos e protegemos suas informações pessoais no EntreNós.
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Última atualização: 9 de abril de 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-rose-100 p-5">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Índice</p>
                <nav className="space-y-1">
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-rose-50 hover:text-rose-600 transition-colors cursor-pointer"
                    >
                      {s.label}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 space-y-10">

              {/* 1. Introdução */}
              <div id="introducao" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl text-white">
                    <i className="ri-information-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">1. Introdução</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    Bem-vindo à Política de Privacidade do <strong className="text-gray-800">EntreNós</strong>. Esta política descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais quando você utiliza nossa plataforma.
                  </p>
                  <p>
                    Ao criar uma conta e utilizar o EntreNós, você concorda com as práticas descritas nesta política. Recomendamos a leitura completa antes de usar nossos serviços.
                  </p>
                  <p>
                    O EntreNós é uma plataforma dedicada a fortalecer relacionamentos amorosos por meio de missões personalizadas, experiências criadas por inteligência artificial e ferramentas de conexão emocional entre casais.
                  </p>
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-shield-check-fill text-rose-500 text-lg flex-shrink-0 mt-0.5"></i>
                      <p className="text-sm text-rose-800">
                        <strong>Compromisso:</strong> Nunca vendemos seus dados pessoais a terceiros. Suas informações são usadas exclusivamente para melhorar sua experiência na plataforma.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Dados Coletados */}
              <div id="dados-coletados" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl text-white">
                    <i className="ri-database-2-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">2. Dados Coletados</h2>
                </div>
                <div className="space-y-6 text-gray-600">
                  <p className="leading-relaxed">Coletamos diferentes tipos de informações para fornecer e melhorar nossos serviços:</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      {
                        icon: 'ri-user-line',
                        color: 'from-rose-500 to-pink-500',
                        title: 'Dados de Cadastro',
                        items: ['Nome completo', 'Endereço de e-mail', 'Senha (criptografada)', 'Data de nascimento', 'Foto de perfil (opcional)'],
                      },
                      {
                        icon: 'ri-heart-line',
                        color: 'from-pink-500 to-purple-500',
                        title: 'Dados do Relacionamento',
                        items: ['Data de início do relacionamento', 'Código de vínculo com parceiro(a)', 'Preferências do casal', 'Objetivos do relacionamento'],
                      },
                      {
                        icon: 'ri-bar-chart-line',
                        color: 'from-purple-500 to-indigo-500',
                        title: 'Dados de Uso',
                        items: ['Missões completadas', 'Pontos e conquistas', 'Histórico de atividades', 'Conversas com a Layla (IA)', 'Fotos do álbum do casal'],
                      },
                      {
                        icon: 'ri-smartphone-line',
                        color: 'from-amber-500 to-rose-500',
                        title: 'Dados Técnicos',
                        items: ['Endereço IP', 'Tipo de dispositivo e navegador', 'Sistema operacional', 'Tokens de notificação push', 'Logs de acesso'],
                      },
                    ].map((block) => (
                      <div key={block.title} className="bg-gray-50 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-8 h-8 flex items-center justify-center bg-gradient-to-br ${block.color} rounded-lg text-white`}>
                            <i className={`${block.icon} text-sm`}></i>
                          </div>
                          <h3 className="font-semibold text-gray-800 text-sm">{block.title}</h3>
                        </div>
                        <ul className="space-y-1.5">
                          {block.items.map((item) => (
                            <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                              <i className="ri-checkbox-circle-line text-green-500 flex-shrink-0"></i>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 3. Uso dos Dados */}
              <div id="uso-dados" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-rose-500 rounded-xl text-white">
                    <i className="ri-settings-3-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">3. Como Usamos Seus Dados</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>Utilizamos suas informações para as seguintes finalidades:</p>
                  <div className="space-y-3">
                    {[
                      { icon: 'ri-magic-line', text: 'Personalizar missões e experiências criadas pela IA Layla com base no perfil do casal.' },
                      { icon: 'ri-notification-3-line', text: 'Enviar notificações sobre novas missões, conquistas e atividades do parceiro(a).' },
                      { icon: 'ri-shield-user-line', text: 'Autenticar sua identidade e garantir a segurança da sua conta.' },
                      { icon: 'ri-bar-chart-2-line', text: 'Calcular pontos, níveis e conquistas do casal.' },
                      { icon: 'ri-customer-service-2-line', text: 'Oferecer suporte técnico e responder dúvidas.' },
                      { icon: 'ri-line-chart-line', text: 'Analisar o uso da plataforma para melhorar funcionalidades e corrigir erros.' },
                      { icon: 'ri-mail-send-line', text: 'Enviar comunicações importantes sobre o serviço (atualizações, mudanças de política).' },
                      { icon: 'ri-vip-crown-line', text: 'Processar pagamentos e gerenciar assinaturas Premium.' },
                    ].map((item) => (
                      <div key={item.text} className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-rose-100 rounded-lg flex-shrink-0">
                          <i className={`${item.icon} text-rose-600 text-sm`}></i>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 4. Compartilhamento */}
              <div id="compartilhamento" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-500 to-amber-500 rounded-xl text-white">
                    <i className="ri-share-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">4. Compartilhamento de Dados</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    <strong className="text-gray-800">Não vendemos seus dados pessoais.</strong> O compartilhamento ocorre apenas nas seguintes situações:
                  </p>
                  <div className="space-y-4">
                    <div className="border-l-4 border-rose-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Com seu(sua) parceiro(a)</h3>
                      <p className="text-sm">Informações como nome, foto de perfil, pontos, missões completadas e conquistas são compartilhadas com o(a) parceiro(a) vinculado(a) à sua conta.</p>
                    </div>
                    <div className="border-l-4 border-pink-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Provedores de Serviço</h3>
                      <p className="text-sm">Utilizamos serviços de terceiros confiáveis como Supabase (banco de dados e autenticação) e processadores de pagamento. Esses parceiros têm acesso limitado aos dados necessários para prestar o serviço.</p>
                    </div>
                    <div className="border-l-4 border-purple-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Obrigações Legais</h3>
                      <p className="text-sm">Podemos divulgar informações quando exigido por lei, ordem judicial ou autoridade governamental competente.</p>
                    </div>
                    <div className="border-l-4 border-amber-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Proteção de Direitos</h3>
                      <p className="text-sm">Em casos de investigação de fraude, violação dos Termos de Uso ou proteção da segurança de usuários.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Segurança */}
              <div id="seguranca" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-500 to-teal-500 rounded-xl text-white">
                    <i className="ri-lock-2-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">5. Segurança dos Dados</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>Adotamos medidas técnicas e organizacionais para proteger suas informações:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-lock-password-line', title: 'Criptografia', desc: 'Senhas armazenadas com hash seguro (bcrypt). Comunicações protegidas por TLS/SSL.' },
                      { icon: 'ri-shield-keyhole-line', title: 'Autenticação Segura', desc: 'Tokens JWT com expiração automática e renovação segura de sessão.' },
                      { icon: 'ri-database-lock-line', title: 'Controle de Acesso', desc: 'Políticas de segurança em nível de linha (RLS) no banco de dados.' },
                      { icon: 'ri-eye-off-line', title: 'Minimização de Dados', desc: 'Coletamos apenas o necessário para o funcionamento da plataforma.' },
                    ].map((item) => (
                      <div key={item.title} className="bg-green-50 rounded-xl p-4 border border-green-100">
                        <div className="flex items-center gap-2 mb-2">
                          <i className={`${item.icon} text-green-600 text-lg`}></i>
                          <h3 className="font-semibold text-gray-800 text-sm">{item.title}</h3>
                        </div>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-amber-600 text-lg flex-shrink-0 mt-0.5"></i>
                      <p className="text-sm text-amber-800">
                        Nenhum sistema é 100% seguro. Em caso de incidente de segurança que afete seus dados, notificaremos você dentro de 72 horas conforme exigido pela LGPD.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Direitos */}
              <div id="direitos" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl text-white">
                    <i className="ri-user-settings-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">6. Seus Direitos (LGPD)</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    De acordo com a <strong className="text-gray-800">Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018)</strong>, você possui os seguintes direitos:
                  </p>
                  <div className="space-y-3">
                    {[
                      { icon: 'ri-eye-line', right: 'Acesso', desc: 'Solicitar uma cópia de todos os dados pessoais que temos sobre você.' },
                      { icon: 'ri-edit-line', right: 'Correção', desc: 'Corrigir dados incompletos, inexatos ou desatualizados.' },
                      { icon: 'ri-delete-bin-line', right: 'Exclusão', desc: 'Solicitar a exclusão dos seus dados pessoais da plataforma.' },
                      { icon: 'ri-download-line', right: 'Portabilidade', desc: 'Receber seus dados em formato estruturado e legível por máquina.' },
                      { icon: 'ri-forbid-line', right: 'Oposição', desc: 'Opor-se ao tratamento de dados em determinadas circunstâncias.' },
                      { icon: 'ri-information-line', right: 'Informação', desc: 'Ser informado sobre com quem compartilhamos seus dados.' },
                      { icon: 'ri-close-circle-line', right: 'Revogação do Consentimento', desc: 'Retirar seu consentimento a qualquer momento, sem prejuízo ao tratamento anterior.' },
                    ].map((item) => (
                      <div key={item.right} className="flex items-start gap-3 bg-purple-50 rounded-xl px-4 py-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-lg flex-shrink-0">
                          <i className={`${item.icon} text-purple-600 text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{item.right}</p>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">
                    Para exercer qualquer um desses direitos, entre em contato pelo e-mail <strong className="text-gray-700">suporteentrenos@outlook.com</strong>. Responderemos em até 15 dias úteis.
                  </p>
                </div>
              </div>

              {/* 7. Cookies */}
              <div id="cookies" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl text-white">
                    <i className="ri-cookie-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">7. Cookies e Tecnologias Similares</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>Utilizamos cookies e tecnologias similares para melhorar sua experiência:</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 rounded-tl-lg">Tipo</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700">Finalidade</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 rounded-tr-lg">Duração</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[
                          { type: 'Essenciais', purpose: 'Autenticação e segurança da sessão', duration: 'Sessão / 7 dias' },
                          { type: 'Funcionais', purpose: 'Preferências de idioma e configurações', duration: '1 ano' },
                          { type: 'Analíticos', purpose: 'Análise de uso e melhoria da plataforma', duration: '90 dias' },
                          { type: 'Push Tokens', purpose: 'Envio de notificações push', duration: 'Até revogação' },
                        ].map((row) => (
                          <tr key={row.type} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-800">{row.type}</td>
                            <td className="px-4 py-3 text-gray-600">{row.purpose}</td>
                            <td className="px-4 py-3 text-gray-500">{row.duration}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-gray-500">
                    Você pode gerenciar cookies nas configurações do seu navegador. Desativar cookies essenciais pode afetar o funcionamento da plataforma.
                  </p>
                </div>
              </div>

              {/* 8. Menores */}
              <div id="menores" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-400 to-pink-400 rounded-xl text-white">
                    <i className="ri-parent-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">8. Menores de Idade</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-red-800 mb-1">Restrição de Idade</p>
                        <p className="text-sm text-red-700">
                          O EntreNós é destinado exclusivamente a pessoas com <strong>18 anos ou mais</strong>. Não coletamos intencionalmente dados de menores de idade.
                        </p>
                      </div>
                    </div>
                  </div>
                  <p>
                    Se tomarmos conhecimento de que coletamos dados de um menor de 18 anos sem o consentimento dos responsáveis legais, excluiremos essas informações imediatamente.
                  </p>
                  <p>
                    Caso você acredite que um menor tenha criado uma conta em nossa plataforma, entre em contato conosco pelo e-mail <strong className="text-gray-800">suporteentrenos@outlook.com</strong>.
                  </p>
                </div>
              </div>

              {/* 9. Alterações */}
              <div id="alteracoes" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl text-white">
                    <i className="ri-refresh-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">9. Alterações nesta Política</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas ou por exigências legais.
                  </p>
                  <p>
                    Quando realizarmos alterações significativas, notificaremos você por:
                  </p>
                  <ul className="space-y-2">
                    {[
                      'E-mail cadastrado na plataforma',
                      'Notificação dentro do aplicativo',
                      'Banner de aviso na página inicial',
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm">
                        <i className="ri-check-line text-green-500 flex-shrink-0"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p>
                    A data da última atualização sempre estará indicada no topo desta página. O uso continuado da plataforma após as alterações constitui aceitação da nova política.
                  </p>
                </div>
              </div>

              {/* 10. Contato */}
              <div id="contato" className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-8 text-white">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold">10. Fale Conosco</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-white/90 leading-relaxed">
                    Se você tiver dúvidas, solicitações ou preocupações sobre esta Política de Privacidade ou sobre o tratamento dos seus dados pessoais, entre em contato com nosso Encarregado de Proteção de Dados (DPO):
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-mail-line', label: 'E-mail', value: 'suporteentrenos@outlook.com' },
                      { icon: 'ri-building-line', label: 'Empresa', value: 'EntreNós' },
                      { icon: 'ri-map-pin-line', label: 'País', value: 'Brasil' },
                      { icon: 'ri-time-line', label: 'Prazo de resposta', value: 'Até 15 dias úteis' },
                    ].map((item) => (
                      <div key={item.label} className="bg-white/15 rounded-xl px-4 py-3">
                        <div className="flex items-center gap-2 mb-1">
                          <i className={`${item.icon} text-white/80 text-sm`}></i>
                          <span className="text-xs text-white/70 font-medium uppercase tracking-wide">{item.label}</span>
                        </div>
                        <p className="text-white font-medium text-sm">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </main>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-rose-950 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-sm text-rose-300">
            <Link to="/" className="hover:text-white transition-colors">Início</Link>
            <span>·</span>
            <Link to="/privacidade" className="text-white font-medium">Privacidade</Link>
            <span>·</span>
            <Link to="/termos" className="hover:text-white transition-colors">Termos de Uso</Link>
          </div>
          <p className="text-rose-300 text-sm">© 2025 EntreNós. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
